import React, { useState, useEffect, useCallback } from 'react';
import LeadForm from '../LeadForm';
import './LeadModal.css';

/**
 * LeadModal
 *
 * Renders a gold "Enquire Now" button fixed to the page.
 * Clicking opens a dark luxury modal with the full LeadForm.
 *
 * Props:
 *   defaultProjectId {number}  – forwarded to LeadForm (1–4)
 *
 * Lenis integration:
 *   Dispatches window events 'lenis:stop' / 'lenis:start'.
 *   In your Lenis setup file add:
 *     window.addEventListener('lenis:stop',  () => lenis.stop());
 *     window.addEventListener('lenis:start', () => lenis.start());
 */
export default function LeadModal({ defaultProjectId = 4 }) {
  const [isOpen, setIsOpen] = useState(false);

  const open  = useCallback(() => setIsOpen(true),  []);
  const close = useCallback(() => setIsOpen(false), []);

  /* ── Escape key ─────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  /* ── Pause / resume Lenis + body scroll ─────────── */
  useEffect(() => {
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('lenis:stop'));
      document.body.style.overflow = 'hidden';
    } else {
      window.dispatchEvent(new CustomEvent('lenis:start'));
      document.body.style.overflow = '';
    }
    return () => {
      window.dispatchEvent(new CustomEvent('lenis:start'));
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* ── Floating trigger ─────────────────────── */}
      <button
        className="lm-trigger"
        onClick={open}
        aria-label="Enquire Now"
      >
        <svg className="lm-trigger__icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path d="M7 1L8.545 5.272L13 5.458L9.636 8.228L10.854 12.5L7 10.082L3.146 12.5L4.364 8.228L1 5.458L5.455 5.272L7 1Z"
            fill="currentColor" />
        </svg>
        <span className="lm-trigger__text">Enquire Now</span>
      </button>

      {/* ── Overlay + Panel ──────────────────────── */}
      {isOpen && (
        <div
          className="lm-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
          role="presentation"
        >
          <div
            className="lm-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Enquiry form"
          >
            {/* Header */}
            <div className="lm-header">
              <div className="lm-header__text">
                <p className="lm-header__eyebrow">Nausiddhi Realty</p>
                <h2 className="lm-header__title">Schedule a Callback</h2>
              </div>
              <button className="lm-close" onClick={close} aria-label="Close enquiry form">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <line x1="1" y1="1" x2="15" y2="15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <line x1="15" y1="1" x2="1"  y2="15" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Gold rule */}
            <div className="lm-rule" aria-hidden="true" />

            {/* Form */}
            <div className="lm-body">
              <LeadForm
                defaultProjectId={defaultProjectId}
                onSuccess={close}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
